import { Tooltip } from '@cotera/client/app/components/ui';
import { cn } from '@cotera/client/v0/lib/utils';
import React, { useState } from 'react';
import type { SpanData, TimeRange } from './types';
import {
  calculateEventPosition,
  formatDuration,
  formatEventType,
  getEventColor,
} from './utils';

// Pure span bar component - no recursion
export interface TraceSpanProps<T = any> {
  spanData: SpanData<T>;
  index: number;
  timeRange: TimeRange;
  showDuration?: boolean;
  isHovered?: boolean;
  onHover?: (spanId: string | null) => void;
  onSpanClick?: (spanId: string) => void;
}

export const TraceSpan = <T = any,>({
  spanData,
  timeRange,
  showDuration = true,
  isHovered = false,
  onHover,
  onSpanClick,
}: TraceSpanProps<T>) => {
  const [hoveredEventId, setHoveredEventId] = useState<string | null>(null);

  const handleMouseEnter = () => {
    onHover?.(spanData.id);
  };

  const handleMouseLeave = () => {
    onHover?.(null);
  };

  const handleClick = () => {
    onSpanClick?.(spanData.id);
  };

  const handleEventMouseEnter = (eventId: string) => {
    setHoveredEventId(eventId);
  };

  const handleEventMouseLeave = () => {
    setHoveredEventId(null);
  };

  return (
    <div
      className="relative h-6 cursor-pointer transition-all duration-200"
      style={{
        left: `${Math.min(
          spanData.left,
          100 - Math.max(spanData.width, 0.5)
        )}%`,
        width: `${Math.max(spanData.width, 0.5)}%`,
        opacity: isHovered ? 1 : 0.9,
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      {/* Single neutral gray line - takes full width */}
      <div className="absolute inset-0 flex items-center">
        <div className="h-0.5 bg-gray-400/50 w-full relative">
          {/* Event markers */}
          {spanData.events?.map((event) => {
            const eventTime = new Date(event.createdAt).getTime();
            const position = calculateEventPosition(
              eventTime,
              spanData.startTime,
              spanData.duration
            );
            const colors = getEventColor(event.eventType);

            return (
              <div
                key={event.id}
                className="absolute top-1/2 -translate-y-1/2"
                style={{
                  left: `${position}%`,
                }}
              >
                <Tooltip
                  side="top"
                  openDelay={0}
                  closeDelay={0}
                  tooltipContent={
                    <div className="text-xs">
                      <div className="font-semibold">
                        {formatEventType(event.eventType)}
                      </div>
                      <div className="text-muted-foreground mt-1">
                        {new Date(event.createdAt).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                          hour12: true,
                        })}
                      </div>
                      {event.metadataId && (
                        <div className="text-muted-foreground mt-1">
                          Metadata: {event.metadataId}
                        </div>
                      )}
                    </div>
                  }
                >
                  {/* Larger hitbox for easier hovering */}
                  <div
                    className="w-6 h-6 -translate-x-1/2 flex items-center justify-center cursor-pointer"
                    style={{
                      zIndex: hoveredEventId === event.id ? 20 : 10,
                    }}
                    onMouseEnter={(e) => {
                      e.stopPropagation();
                      handleEventMouseEnter(event.id);
                    }}
                    onMouseLeave={(e) => {
                      e.stopPropagation();
                      handleEventMouseLeave();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    {/* Small visual marker inside the hitbox */}
                    <div
                      className={cn(
                        'w-1.5 h-1.5 rounded-full ring-2 transition-transform',
                        hoveredEventId === event.id && 'scale-125',
                        colors.bg,
                        colors.ring
                      )}
                    />
                  </div>
                </Tooltip>
              </div>
            );
          })}
        </div>
      </div>

      {/* Duration label positioned to the right of the span */}
      {showDuration && (
        <div
          className="absolute top-1/2 -translate-y-1/2 text-xs text-muted-foreground font-medium whitespace-nowrap pointer-events-none"
          style={{
            left: '100%',
            paddingLeft: '8px',
          }}
        >
          {formatDuration(spanData.duration, timeRange.unit)}
        </div>
      )}
    </div>
  );
};

// Span group component that handles recursion
export interface SpanGroupProps<T = any> {
  spanData: SpanData<T>;
  index: number;
  timeRange: TimeRange;
  showDuration?: boolean;
  isHovered?: boolean;
  onHover?: (spanId: string | null) => void;
  onSpanClick?: (spanId: string) => void;
  renderSpan?: (
    spanData: SpanData<T>,
    index: number,
    timeRange: TimeRange
  ) => React.ReactNode;
}

export const SpanGroup = <T = any,>({
  spanData,
  index,
  timeRange,
  showDuration = true,
  isHovered = false,
  onHover,
  onSpanClick,
  renderSpan,
}: SpanGroupProps<T>) => {
  return (
    <div className="relative flex flex-col gap-2">
      {/* Render the current span */}
      {renderSpan ? (
        <div
          onMouseEnter={() => onHover?.(spanData.id)}
          onMouseLeave={() => onHover?.(null)}
          onClick={() => onSpanClick?.(spanData.id)}
        >
          {renderSpan(spanData, index, timeRange)}
        </div>
      ) : (
        <TraceSpan
          spanData={spanData}
          index={index}
          timeRange={timeRange}
          showDuration={showDuration}
          isHovered={isHovered}
          onHover={onHover}
          onSpanClick={onSpanClick}
        />
      )}

      {/* Render children */}
      {spanData.children !== undefined && spanData.children.length > 0 && (
        <div>
          {spanData.children.map((child, childIndex) => (
            <SpanGroup
              key={child.id}
              spanData={child}
              index={childIndex}
              timeRange={timeRange}
              showDuration={showDuration}
              isHovered={isHovered}
              onHover={onHover}
              onSpanClick={onSpanClick}
              renderSpan={renderSpan}
            />
          ))}
        </div>
      )}
    </div>
  );
};
